"use client";

import classNames from "classnames/bind";
import {
  type ChangeEvent,
  forwardRef,
  type NamedExoticComponent,
  useId
} from "react";

import type { InputProps } from "../../props/input-props";
import styles from "./radio-button.module.scss";
import { useRadioGroupContext } from "./radio-group-context";
const cx = classNames.bind(styles);

type Props = {
  value: string;
};

export type RadioButtonProps = Props &
  Omit<InputProps, "name" | "errorId" | "htmlFor">;

export const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  ({ label, disabled, className, hideLabel, value, ...rest }, ref) => {
    const id = useId();
    const {
      disabled: isAllDisabled,
      error,
      name,
      onChange: onChangeFromContext,
      selectedValue
    } = useRadioGroupContext();
    const isDisabled = disabled || isAllDisabled;
    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
      rest.onChange?.(event);
      onChangeFromContext?.(value);
    };
    return (
      <div className={cx("design-radiobutton-container", className, { error })}>
        <input
          {...rest}
          className={cx("design-radiobutton-input")}
          disabled={isDisabled}
          aria-label={label as string}
          type="radio"
          id={id}
          value={value}
          checked={selectedValue === value}
          name={name}
          ref={ref}
          onChange={handleOnChange}
        />

        <div className={cx("design-radiobutton-label-container")}>
          <div className={cx("design-radiobutton-circle")}></div>
          {!hideLabel && (
            <label
              htmlFor={id}
              className={cx("design-radiobutton-label", {
                disabled: isDisabled
              })}
            >
              {label}
            </label>
          )}
        </div>
      </div>
    );
  }
);

(RadioButton as NamedExoticComponent).displayName = "RadioButton";
